// useCancellableEffect.tsx
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import { useEffect } from 'react'
import axios, { CancelToken } from 'axios'

export type CancellableEffect = (token: CancelToken) => void | (() => void)

/**
 * Custom React hook that runs an async effect with a cancellable Axios token.
 * The effect receives an Axios CancelToken and can optionally return a cleanup function.
 * The cancel token is automatically cancelled when the component unmounts or dependencies change.
 *
 * @param effect - An async function that receives an Axios CancelToken and performs side effects. May return a cleanup function.
 * @param dependencies - Dependency list that controls when the effect is re-run.
 *
 * @example
 * useCancellableEffect(async (cancelToken) => {
 *   try {
 *     const response = await axios.get('/api/data', { cancelToken });
 *     setData(response.data);
 *   } catch (error) {
 *     if (axios.isCancel(error)) {
 *       console.log('Request cancelled');
 *     }
 *   }
 *   // Optional cleanup
 *   return () => {
 *     setData(null);
 *   };
 * }, [someDependency]);
 */
export function useCancellableEffect(
  effect: CancellableEffect,
  dependencies: React.DependencyList
) {
  useEffect(() => {
    const source = axios.CancelToken.source()
    const cleanup = effect(source.token)

    return () => {
      source.cancel()
      if (cleanup && typeof cleanup === 'function') {
        cleanup()
      }
    }
  }, dependencies)
}
